import { useEffect } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SetupCoachmark } from "./SetupCoachmark";
import { Sidebar, useSidebarSurface } from "../Sidebar";
import { useSelfSetupTour } from "../../hooks/useSelfSetupTour";
import type { SelfSetupTourStep } from "../../utils/selfSetupTour";

type NavTourTarget = {
  path: string;
  text: string;
  icon: React.ReactNode;
  label: string;
  selected: boolean;
};

function stepForPath(path: string): SelfSetupTourStep | null {
  if (path === "/supply") return "supply";
  if (path === "/dashboard") return "dashboard";
  if (path === "/orders") return "orders";
  return null;
}

function navIdForStep(step: SelfSetupTourStep): string {
  if (step === "supply") return "self-setup-nav-supply";
  if (step === "dashboard") return "self-setup-nav-dashboard";
  return "self-setup-nav-orders";
}

export function SelfSetupSidebarEffect({
  onExpandDesktop,
  onOpenMobile,
}: {
  onExpandDesktop: () => void;
  onOpenMobile: () => void;
}) {
  const { activeStep, tourPhase } = useSelfSetupTour();

  useEffect(() => {
    if (!activeStep || tourPhase !== "nav") return;
    onExpandDesktop();
    onOpenMobile();
  }, [activeStep, tourPhase, onExpandDesktop, onOpenMobile]);

  return null;
}

export function SelfSetupNavItem({
  item,
  onNavigate,
}: {
  item: NavTourTarget;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const surface = useSidebarSurface();
  const { isStep, tourPhase, handleNavTourClick, dismissTour } =
    useSelfSetupTour();
  const tourStep = stepForPath(item.path);
  const tipActive = Boolean(
    tourStep && isStep(tourStep) && tourPhase === "nav",
  );
  const showTip =
    tipActive &&
    (isMobileViewport ? surface === "mobile" : surface === "desktop");

  const navItem = (
    <Sidebar.NavItem
      id={tourStep && showTip ? navIdForStep(tourStep) : undefined}
      icon={item.icon}
      label={item.label}
      selected={item.selected}
      onClick={() => {
        if (tourStep && isStep(tourStep) && tourPhase === "nav") {
          handleNavTourClick(tourStep);
          return;
        }
        onNavigate(item.path);
      }}
    />
  );

  if (!showTip || !tourStep) {
    return navItem;
  }

  return (
    <SetupCoachmark
      open
      variant="sidebar"
      title={t(`selfSetup.tour.${tourStep}NavTitle`)}
      subtitle={t(`selfSetup.tour.${tourStep}NavSubtitle`)}
      onDismiss={dismissTour}
    >
      {navItem}
    </SetupCoachmark>
  );
}
